// Alcance por dimensión del mock (`.dim-chips-grid`): 4 columnas de chips, una por dimensión, con
// su color. Lo comparten Políticas y Promociones; cada pantalla arma sus columnas con estas piezas.
import type { ReactNode } from 'react';
import { Search } from 'lucide-react';
import { HelpHint } from '@/components/ui/HelpHint';
import { cn } from '@/lib/cn';

export type ScopeDim = 'segment' | 'tier' | 'store' | 'product' | 'retailer';

/** El color del dato, cuando la opción tiene uno propio. Sin él manda el color de la dimensión. */
export interface ScopeChipTone {
    chip: string;
    dot: string;
}

/** El color de la DIMENSIÓN: el de la opción que no tiene uno propio, como «Todas las tiendas». */
const DIM_SELECTED_CLASS: Record<ScopeDim, ScopeChipTone> = {
    segment: {
        chip: 'border-segment-soft-border bg-segment-soft text-segment',
        dot: 'border-segment bg-segment',
    },
    tier: { chip: 'border-info/40 bg-info/10 text-info', dot: 'border-info bg-info' },
    store: {
        chip: 'border-success/40 bg-success/10 text-success',
        dot: 'border-success bg-success',
    },
    product: {
        chip: 'border-warning/40 bg-warning/10 text-warning',
        dot: 'border-warning bg-warning',
    },
    retailer: {
        chip: 'border-primary/40 bg-primary/10 text-primary',
        dot: 'border-primary bg-primary',
    },
};

const CHIP_BASE =
    'flex items-center gap-2 rounded-[5px] border px-2 py-[5px] text-left text-[11.5px] transition-colors';

const CHIP_OFF = 'border-border bg-card text-foreground hover:bg-accent/50';

export function ScopeChipDot({
    dim,
    on,
    tone,
}: {
    dim: ScopeDim;
    on: boolean;
    tone?: ScopeChipTone;
}) {
    // El punto del dato va sin borde: sus clases solo traen relleno, y así el color ocupa el disco
    // entero en vez de dejar un anillo del color heredado.
    const onClass = tone ? cn(tone.dot, 'border-0') : DIM_SELECTED_CLASS[dim].dot;
    return (
        <span
            aria-hidden
            className={cn(
                'box-border size-2.5 flex-none rounded-full border-[1.5px] transition-colors',
                on ? onClass : 'border-border bg-transparent',
            )}
        />
    );
}

/**
 * Chip que prende y apaga una opción de la dimensión (`.dim-chip`). `detail` es el renglón chico de
 * abajo — el id de la tienda, el SKU — que distingue dos opciones que se llaman igual.
 */
export function ScopeChip({
    dim,
    label,
    detail,
    count,
    selected,
    disabled,
    tone,
    tooltip,
    asRadio = false,
    onToggle,
}: {
    dim: ScopeDim;
    label: string;
    detail?: string;
    /** El texto del tooltip; sin él, el nombre y el `detail`. */
    tooltip?: string;
    /** Dentro de un `radiogroup`: se anuncia como radio con `aria-checked`. */
    asRadio?: boolean;
    /** Cuántos del catálogo caen en la opción (`.chip-count`); sin él no se pinta. */
    count?: number;
    selected: boolean;
    disabled?: boolean;
    /** El color del dato — el metal del nivel, el slot del segmento. Sin él manda la dimensión. */
    tone?: ScopeChipTone;
    onToggle: () => void;
}) {
    return (
        <button
            type="button"
            role={asRadio ? 'radio' : undefined}
            aria-checked={asRadio ? selected : undefined}
            aria-pressed={asRadio ? undefined : selected}
            disabled={disabled}
            onClick={onToggle}
            title={tooltip ?? (detail ? `${label} · ${detail}` : label)}
            className={cn(
                CHIP_BASE,
                'disabled:opacity-60',
                selected
                    ? cn('font-semibold', tone?.chip ?? DIM_SELECTED_CLASS[dim].chip)
                    : CHIP_OFF,
            )}
        >
            <ScopeChipDot dim={dim} on={selected} tone={tone} />
            <span className="min-w-0 flex-1">
                <span className="block truncate">{label}</span>
                {detail ? (
                    <span className="block truncate text-[9.5px] font-normal text-muted-foreground">
                        {detail}
                    </span>
                ) : null}
            </span>
            {count === undefined ? null : (
                <span
                    aria-hidden
                    className="flex-none text-[10px] font-normal tabular-nums text-muted-foreground"
                >
                    {count}
                </span>
            )}
        </button>
    );
}

/** Un chip prendido que no se puede tocar: el dato fijo de la columna. */
export function ScopeChipStatic({ dim, label }: { dim: ScopeDim; label: string }) {
    return (
        <span className={cn(CHIP_BASE, 'font-semibold', DIM_SELECTED_CLASS[dim].chip)}>
            <ScopeChipDot dim={dim} on />
            <span className="min-w-0 flex-1 truncate">{label}</span>
        </span>
    );
}

/** Una columna del alcance (`.dim-group`): título, campo chico opcional y la lista de chips. */
export function ScopeChipGroup({
    title,
    help,
    search,
    footer,
    listRole,
    describedBy,
    invalid,
    children,
}: {
    title: string;
    /** El id del mensaje de error de la columna. */
    describedBy?: string;
    invalid?: boolean;
    /** `radiogroup` cuando la columna se elige de a uno. */
    listRole?: 'radiogroup';
    /** Qué filtra la columna; va en su «?». */
    help?: string;
    /** El campo chico que va arriba de la lista (`.dim-search`). */
    search?: ReactNode;
    /** Nota al pie de la columna, fuera del área que scrollea. */
    footer?: ReactNode;
    children: ReactNode;
}) {
    return (
        <section
            className="min-w-0 rounded-md bg-muted/30 p-2.5"
            aria-label={title}
            aria-describedby={describedBy}
        >
            <div className="mb-2 flex items-center gap-1">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                    {title}
                </h3>
                {help ? <HelpHint text={help} label={`Ayuda: ${title}`} asSpan /> : null}
            </div>
            {search}
            <div
                role={listRole ?? (invalid ? 'group' : undefined)}
                aria-label={listRole || invalid ? title : undefined}
                aria-invalid={invalid ? true : undefined}
                aria-describedby={invalid ? describedBy : undefined}
                className="flex max-h-[165px] flex-col gap-1 overflow-y-auto"
            >
                {children}
            </div>
            {footer}
        </section>
    );
}

const GRID_COLUMNS = { 4: 'grid-cols-4', 5: 'grid-cols-5' } as const;

/** `.dim-chips-grid`: las columnas del alcance; cinco cuando se suma la de cadena. */
export function ScopeChipGrid({
    columns = 4,
    children,
}: {
    columns?: keyof typeof GRID_COLUMNS;
    children: ReactNode;
}) {
    return <div className={cn('grid gap-2.5', GRID_COLUMNS[columns])}>{children}</div>;
}

/**
 * El buscador chico del portal (`.search-x` del mock, la caja estándar de TODA búsqueda del Portal
 * Admin SK): filtra lo que ya está en pantalla y se limpia con su ×. Nació en la columna de chips
 * del alcance (`.dim-search`), de ahí el nombre.
 */
export function ScopeChipSearch({
    value,
    placeholder,
    label,
    disabled,
    withIcon = false,
    className,
    inputClassName,
    onChange,
    onSubmit,
}: {
    value: string;
    placeholder: string;
    /** Cómo se nombra el campo para un lector de pantalla; sin él, el placeholder. */
    label?: string;
    disabled?: boolean;
    /** La lupa a la izquierda (`.s-icon`). Las cajas dentro de una columna de chips no la llevan. */
    withIcon?: boolean;
    className?: string;
    /** Ajustes de ESTA caja sobre el input: el mock le da otro cuerpo según dónde vive. */
    inputClassName?: string;
    onChange: (value: string) => void;
    /** Enter fuerza la acción de ESTA caja. Sin él, la tecla sigue sin hacer nada. */
    onSubmit?: () => void;
}) {
    const name = label ?? placeholder;
    return (
        <div className={cn('relative mb-1.5', className)}>
            {withIcon ? (
                <Search
                    aria-hidden
                    className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                />
            ) : null}
            <input
                type="search"
                aria-label={name}
                placeholder={placeholder}
                disabled={disabled}
                value={value}
                onChange={(event) => {
                    onChange(event.target.value);
                }}
                onKeyDown={
                    onSubmit === undefined
                        ? undefined
                        : (event) => {
                              if (event.key !== 'Enter') return;
                              event.preventDefault();
                              onSubmit();
                          }
                }
                className={cn(
                    'w-full rounded border border-border bg-background py-1 pr-6 text-[10.5px] text-foreground outline-none placeholder:text-[10.5px] focus:border-ring focus:ring-2 focus:ring-ring/15 disabled:opacity-60 [&::-webkit-search-cancel-button]:hidden',
                    withIcon ? 'pl-6' : 'pl-2',
                    inputClassName,
                )}
            />
            {value === '' ? null : (
                <button
                    type="button"
                    aria-label={`Limpiar ${name}`}
                    onClick={() => {
                        onChange('');
                    }}
                    className="absolute right-1 top-1/2 inline-flex size-4 -translate-y-1/2 items-center justify-center rounded text-[11px] leading-none text-muted-foreground transition-colors hover:bg-muted"
                >
                    ×
                </button>
            )}
        </div>
    );
}
